<template>
  <ContentWrapper :add-padding="false">
    <fieldset :disabled="isGenerating">
      <form @submit.self="formSubmit">
        <Collapsable :toggle-on-mount="false" title="Manual Control of Inputs">
          <p>
          Manually control the various inputs the model uses when generating an image. Use the <b>AUTO</b> panel if you don't want this flexibility. Press
            the
            <i>Help</i>-buttons associated with each field to learn more about
            their effect on the generated images.

            <p><i>All calculations are performed locally on your device, and generating an image with a large <b>Resolution</b>
            might, depending on your browser and hardware, take long. It is thus recommended
            to start with lower resolutions, and gradually increase it to avoid freezing the browser.</i></p>
          </p>
        </Collapsable>
        <FormRange
          name="imgDim"
          min="10"
          max="1000"
          step="10"
          :value.sync="imgDim"
          label="Resolution"
          :formatValue="value => `${value}x${value} pixels`"
        >
          <Tooltip slot="tooltip"
            >The model outputs only one pixel at a time, effectively allowing
            for the generation of infinite-resolution images. Try keeping the
            <b>Latent Noise</b> fixed while going from a low resolution, e.g.,
            30x30, to a high resolution, e.g., 400x400, and observe the
            different visual properties that arise at different
            resolutions.</Tooltip
          >
        </FormRange>

        <FormRange
          name="scale"
          min="0.01"
          max="10"
          step="0.01"
          :value.sync="scale"
          label="Zoom"
          :formatValue="value => parseFloat(scale).toFixed(2) + 'x'"
        >
          <Tooltip slot="tooltip"
            ><b>Zoom</b> allows you to generate zoomed-in or zoomed-out versions
            of the generated images. A value larger than 1 will zoom in, while a
            value smaller than 1 will zoom out.</Tooltip
          >
        </FormRange>

        <FormRange
          name="firstCategory"
          min="0"
          max="9"
          step="1"
          :value.sync="firstCategory"
          label="Style"
          :formatValue="value => parseInt(firstCategory) + 1"
        >
          <Tooltip slot="tooltip"
            >There are 10 different <b>Styles</b>, and each <b>Style</b> usually
            corresponds to a distinct set of visual features, e.g., genders,
            objects. Changing the <b>Style</b> leads to larger changes compared
            to tweaking the <b>Details</b>.</Tooltip
          >
        </FormRange>

        <FormRange
          name="firstContinuous"
          min="-2"
          max="2"
          step="0.1"
          :value.sync="firstContinuous"
          label="Detail #1"
          :formatValue="value => parseFloat(firstContinuous).toFixed(1)"
        >
          <Tooltip slot="tooltip"
            >Changing the <b>Details</b> often lead to small visual changes in
            the size, rotation, color, etc., of the objects in the image. The
            larger the magnitude, the stronger the effect: if a large positive
            value rotates the object one way, a large negative value rotates it
            the other way.</Tooltip
          >
        </FormRange>

        <FormRange
          name="secondContinuous"
          min="-2"
          max="2"
          step="0.1"
          :value.sync="secondContinuous"
          label="Detail #2"
          :formatValue="value => parseFloat(secondContinuous).toFixed(1)"
        >
          <Tooltip slot="tooltip"
            >Changing the <b>Details</b> often lead to small visual changes in
            the size, rotation, color, etc., of the objects in the image. The
            larger the magnitude, the stronger the effect: if a large positive
            value rotates the object one way, a large negative value rotates it
            the other way.</Tooltip
          >
        </FormRange>

        <FormCheckbox
          name="fixedLatentNoise"
          :value.sync="fixedLatentNoise"
          label="Fixed Latent Noise"
          :formatValue="value => (fixedLatentNoise === true ? 'Yes' : 'No')"
        >
          <Tooltip slot="tooltip"
            >The <b>Latent Noise</b> is the most influential input to the
            network, and can in a way be considered the "identity" of each
            generated image. Keep this fixed and play around with the other
            parameters to effectively generate different versions of the same
            image.</Tooltip
          >
        </FormCheckbox>

        <FormElement :vertical="false">
          <button class="form-button" type="submit">Generate Image</button>
          <button class="form-button" @click.stop.prevent="reset" type="button">
            Reset
          </button>
        </FormElement>
      </form>
    </fieldset>
  </ContentWrapper>
</template>

<script>
import Tooltip from "./Tooltip.vue"
import FormElement from "./FormElement.vue"
import FormRange from "./FormRange.vue"
import FormCheckbox from "./FormCheckbox.vue"
import Collapsable from "./Collapsable.vue"
import ContentWrapper from "./ContentWrapper.vue"

export default {
  name: "Form",
  components: {
    Collapsable,
    ContentWrapper,
    Tooltip,
    FormRange,
    FormCheckbox,
    FormElement
  },
  props: ["isGenerating"],
  data() {
    return {
      testValue: 50,
      imgDim: 30,
      firstCategory: 8,
      firstContinuous: 0,
      fixedLatentNoise: true,
      secondContinuous: 0,
      scale: 1
    }
  },
  methods: {
    formSubmit(e) {
      e.preventDefault()
      const {
        imgDim,
        firstCategory,
        firstContinuous,
        secondContinuous,
        fixedLatentNoise,
        scale
      } = this
      const config = {
        fixedLatentNoise,
        imgDim: parseFloat(imgDim),
        firstCategory: parseFloat(firstCategory),
        firstContinuous: parseFloat(firstContinuous),
        secondContinuous: parseFloat(secondContinuous),
        scale: 10 / parseFloat(scale)
      }

      this.$ga.event({
        eventCategory: 'manual',
        eventAction: 'generate',
        eventValue: 1
      })

      this.$emit("update", config)
      // window.console.log(config)
    },
    reset() {
      this.imgDim = 30
      this.firstCategory = 8
      this.firstContinuous = 0
      this.fixedLatentNoise = true
      this.secondContinuous = 0
      this.scale = 1
      this.$ga.event({
        eventCategory: 'manual',
        eventAction: 'reset',
      })
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
@import "../style/variables.scss";
fieldset {
}

form {
  background-color: $color-dark-lighter;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
}

@media (max-width: 767px) {
  fieldset {
    width: 100%;
  }
  form {
    flex-grow: 1;
    flex-direction: column;
  }
}
</style>
